import React from 'react';
import { toast } from 'react-toastify';
import type TreeIndex from '../../../../classes/pieces/TreeIndex';
import type TreeRowFilter from '../../../../classes/pieces/TreeRowFilter';
import TreeNodeType from '../../../../constants/liquidation/TreeNodeType';
import type { TreeRow } from '../../../../interfaces/pieces/TreeRow';
import useLiquidationTreeviewStore from '../../../../stores/liquidationTreeviewStore';

const { getState } = useLiquidationTreeviewStore;

export const MESSAGE_ENTITE_VIDE =
	'Aucun bordereau de ce budget ne correspond aux critères.';
export const MESSAGE_ENTITES_VIDES =
	'Aucun bordereau de ces budgets ne correspond aux critères.';

interface EntitesVideesParFiltreOptions {
	anneeExercice: number;
	index: TreeIndex;
	expandedKeys: ReadonlySet<string>;
	loadingKeys: ReadonlySet<string>;
	treeRowFilter: TreeRowFilter;
}

/**
 * Replie une entité dépliée dont aucun bordereau ne passe les filtres. Un message le dit seulement si c'est le
 * fait de l'utilisateur (entité qu'il vient de déplier, ou filtres qu'il vient de changer) et que ce sont les
 * filtres qui la vident (elle a des bordereaux, aucun ne passe) : pas après une relecture en arrière-plan.
 *
 * @returns à appeler quand l'utilisateur déplie une ligne (chevron ou clic)
 */
const useEntitesVideesParFiltre = ({
	anneeExercice,
	index,
	expandedKeys,
	loadingKeys,
	treeRowFilter,
}: EntitesVideesParFiltreOptions): ((row: TreeRow) => void) => {
	/** Entités que l'utilisateur vient de déplier, en attente de leur chargement. */
	const deplieesParUtilisateur = React.useRef(new Set<string>());
	const filtreApplique = React.useRef(treeRowFilter);

	React.useEffect(() => {
		const isFiltreChange = filtreApplique.current !== treeRowFilter;
		filtreApplique.current = treeRowFilter;

		const depliees = index
			.getRoots()
			.filter(
				(entite) =>
					entite.nodeType === TreeNodeType.ENTITE &&
					expandedKeys.has(entite.rowKey) &&
					!loadingKeys.has(entite.rowKey),
			);
		const vides = depliees.filter(
			(entite) =>
				!(index.getChildren(entite.rowKey) ?? []).some((child) =>
					treeRowFilter.accepts(child),
				),
		);
		const videesParFiltre = vides.filter(
			(entite) =>
				(isFiltreChange || deplieesParUtilisateur.current.has(entite.rowKey)) &&
				Boolean(index.getChildKeys(entite.rowKey)?.length),
		);
		// entités chargées et dépliées : traitées, plus de message pour elles avant un nouveau dépliage
		depliees.forEach((entite) => {
			deplieesParUtilisateur.current.delete(entite.rowKey);
		});
		if (!vides.length) return;

		getState().removeExpanded(
			anneeExercice,
			vides.map((entite) => entite.rowKey),
		);
		if (treeRowFilter.isActive && videesParFiltre.length) {
			toast.info(
				videesParFiltre.length > 1
					? MESSAGE_ENTITES_VIDES
					: MESSAGE_ENTITE_VIDE,
			);
		}
	}, [index, expandedKeys, loadingKeys, treeRowFilter, anneeExercice]);

	return React.useCallback((row: TreeRow) => {
		if (row.nodeType === TreeNodeType.ENTITE) {
			deplieesParUtilisateur.current.add(row.rowKey);
		}
	}, []);
};

export default useEntitesVideesParFiltre;
